import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ToolbarTool from '../../../ToolbarTool';
import Icon from '../../../../../../components/icons/Icon';
import SheetContainer, {
    CloseButton,
    Content,
    Header,
    Heading,
    Overlay,
    Sheet,
} from '../../../../../../mobile/structural/sheet';
import useSheet from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { SheetId } from '../../../../../../mobile/structural/sheet/sheetTypes';
import { canShowLabelsToolSelector } from '../labelToolSelectors';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { Label } from '../../../../../../../common/labels/labelTypes';
import LabelEditSheet from './LabelEditSheet';
import { selectUsableLabels } from '../../../../../../element/labels/labelSelectors';
import LabelDeleteSheet from './LabelDeleteSheet';
import LabelsSearchBar from '../components/LabelsSearchBar';
import LabelLists from '../components/LabelLists';
import useLabelsTool from '../hooks/useLabelsTool';
import useBecameTrueEffect from '../../../../../../utils/react/useBecameTrueEffect';
import { deleteLabel } from '../../../../../../../common/labels/labelActions';
import LabelActionsSheet from './LabelActionsSheet';
import { useLabelUsageData } from '../labelUsage/useLabelUsageData';

type MobileLabelsToolProps = {
    currentUserId: string;
    gridSize: number;
    name: string;
    selectedElements: Immutable.List<ImMNElement>;
};

const MobileLabelsTool = (props: MobileLabelsToolProps): ReactElement | null => {
    const { currentUserId, gridSize, name } = props;

    const dispatch = useDispatch();
    const canShowLabelsTool = useSelector(canShowLabelsToolSelector);
    const sheetProps = useSheet(SheetId.LabelsTool);
    const actionSheetProps = useSheet(SheetId.LabelActionsTool);
    const editSheetProps = useSheet(SheetId.LabelEditTool);
    const deleteSheetProps = useSheet(SheetId.LabelDeleteTool);
    const { isSheetOpen, dispatchOpenSheet } = sheetProps;
    const [currentlyEditingLabelId, setCurrentlyEditingLabelId] = useState<string | null>(null);
    const [editingLabelSnapshot, setEditingLabelSnapshot] = useState<Label | null>(null);
    const labelMap = useSelector(selectUsableLabels);
    const currentlyEditingLabel = labelMap?.[currentlyEditingLabelId || ''] || editingLabelSnapshot;
    const isLabelFlowOpen = sheetProps.isSheetOpen || currentlyEditingLabelId;
    const usageDataState = useLabelUsageData(currentlyEditingLabelId);

    const {
        allLabels,
        elements,
        searchString,
        setSearchString,
        handleCreateLabel,
        handleSearchSubmit,
        handleToggleLabelSelection,
    } = useLabelsTool({ currentUserId, isActive: isSheetOpen });

    const handleClickActions = (label: Label) => {
        setCurrentlyEditingLabelId(label._id);
        setEditingLabelSnapshot(label);
        actionSheetProps.dispatchOpenSheet();
    };

    const handleOpenEditSheet = () => {
        editSheetProps.dispatchOpenSheet();
    };

    useEffect(() => {
        if (!sheetProps.isSheetOpen || !currentlyEditingLabelId) return;

        setCurrentlyEditingLabelId(null);
        setEditingLabelSnapshot(null);
    }, [currentlyEditingLabelId, sheetProps.isSheetOpen]);

    useBecameTrueEffect(() => {
        setSearchString('');
    }, !isLabelFlowOpen);

    const handleOpenLabelsSheet = () => {
        dispatchOpenSheet();
    };

    const handleOpenDeleteSheet = () => {
        deleteSheetProps.dispatchOpenSheet();
    };

    const handleDeleteLabel = () => {
        if (currentlyEditingLabelId) {
            dispatch(deleteLabel(currentlyEditingLabelId));
        }

        // Reset the flow history so closing the root labels sheet does not restore the delete confirmation.
        deleteSheetProps.dispatchCloseSheet({ skipPreviousSheetRestore: true });

        sheetProps.dispatchOpenSheet();
    };

    if (!canShowLabelsTool) return null;

    return (
        <>
            <ToolbarTool name={name} isOpen={isSheetOpen} onClick={handleOpenLabelsSheet}>
                <Icon gridSize={gridSize} name="toolbar-label" />
            </ToolbarTool>
            <SheetContainer
                className="LabelsToolbarSheet"
                {...sheetProps}
                defaultSnapPoint={0.5}
                snapPoints={[0.5, 0.9]}
            >
                <Overlay />
                <Sheet>
                    <Header>
                        <Heading>{name}</Heading>
                        <CloseButton />
                    </Header>
                    <LabelsSearchBar
                        value={searchString}
                        onChange={setSearchString}
                        handleSearchSubmit={handleSearchSubmit}
                        autoFocus={false}
                        className="!px-4"
                        size="lg"
                    />
                    <Content className="px-3.5 py-1.5">
                        <LabelLists
                            handleToggleLabelSelection={handleToggleLabelSelection}
                            handleCreateLabel={handleCreateLabel}
                            elements={elements}
                            searchString={searchString}
                            allLabels={allLabels}
                            handleClickEdit={handleClickActions}
                            isSheet
                            size="lg"
                        />
                    </Content>
                </Sheet>
            </SheetContainer>
            {currentlyEditingLabel && (
                <>
                    <LabelActionsSheet
                        label={currentlyEditingLabel}
                        onEditClick={handleOpenEditSheet}
                        onDeleteClick={handleOpenDeleteSheet}
                        sheetProps={actionSheetProps}
                        usageDataState={usageDataState}
                    />
                    <LabelEditSheet label={currentlyEditingLabel} sheetProps={editSheetProps} />
                    <LabelDeleteSheet onDelete={handleDeleteLabel} sheetProps={deleteSheetProps} />
                </>
            )}
        </>
    );
};

export default MobileLabelsTool;
